import { useEffect } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
  twitterCard?: string;
  schemaData?: Record<string, unknown>;
  articlePublishedTime?: string;
  articleModifiedTime?: string;
  articleAuthor?: string;
  articleTags?: string[];
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogImage,
  ogType = 'website',
  twitterCard = 'summary_large_image',
  schemaData,
  articlePublishedTime,
  articleModifiedTime,
  articleAuthor,
  articleTags,
}: SEOHeadProps) {
  const siteName = '精品视频精选平台';
  const baseUrl = 'https://www.jingpinsp.com';
  const finalTitle = title.includes(siteName) ? title : `${title} - ${siteName}`;

  useEffect(() => {
    document.title = finalTitle;

    const setMeta = (name: string, content: string, property = false) => {
      const attr = property ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
    };

    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);
    if (canonicalUrl) setLink('canonical', canonicalUrl);

    const ogTitleFinal = ogTitle || finalTitle;
    const ogDescFinal = ogDescription || description;
    setMeta('og:title', ogTitleFinal, true);
    setMeta('og:description', ogDescFinal, true);
    setMeta('og:type', ogType, true);
    setMeta('og:site_name', siteName, true);
    setMeta('og:url', canonicalUrl || baseUrl, true);
    if (ogImage) setMeta('og:image', ogImage, true);

    setMeta('twitter:card', twitterCard);
    setMeta('twitter:title', ogTitleFinal);
    setMeta('twitter:description', ogDescFinal);
    if (ogImage) setMeta('twitter:image', ogImage);

    if (articlePublishedTime) setMeta('article:published_time', articlePublishedTime);
    if (articleModifiedTime) setMeta('article:modified_time', articleModifiedTime);
    if (articleAuthor) setMeta('article:author', articleAuthor);
    if (articleTags) {
      articleTags.forEach((tag) => {
        let el = document.querySelector(`meta[property="article:tag"][content="${tag}"]`);
        if (!el) {
          el = document.createElement('meta');
          el.setAttribute('property', 'article:tag');
          el.setAttribute('content', tag);
          document.head.appendChild(el);
        }
      });
    }

    const existingSchema = document.getElementById('json-ld-schema');
    if (existingSchema && schemaData) {
      existingSchema.textContent = JSON.stringify(schemaData);
    } else if (!existingSchema && schemaData) {
      const script = document.createElement('script');
      script.id = 'json-ld-schema';
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schemaData);
      document.head.appendChild(script);
    }
  }, []);

  return null;
}